/**
 * CashRepatriation.js — the Cash Repatriation report, as its own page.
 *
 * Brian, 21 September 2026: *"the cash repatriation report is so good that it
 * should actually be its own tab under NAV/Performance"*, and then, when it
 * had been built as a tab *inside* the NAV page: *"its still not there on its
 * own tab below NAV/Performance"*. It is a sidebar entry directly beneath
 * NAV / Performance, not a tab within it.
 *
 * The view itself lives in `components/RepatriationReport.js` and is shared
 * with the report centre, so there is one copy of the figures and one spelling
 * of `n/a` (CLAUDE.md §0.30). This page is the chrome around it: the date, the
 * refresh, and the loading and error states.
 *
 * Brian, 7 October 2026, on what this page is for: *"cash owed is whenever we
 * generate proceeds that are directly attributable to a deal. Whether thats
 * trading proceeds, dividends, interest (cash), redemption proceeds, etc.
 * Thats what the sheet should show. Not just cash moved from prime brokers to
 * bank accounts"* (D-251).
 *
 * It shows no verdict. `target_percentile` is the reader's own threshold and
 * has no default, because the percentile is a fact about the past and no code
 * tests the future — see the service docstring for why that line is
 * load-bearing.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import api from "../api/client";
import RepatriationView from "../components/RepatriationReport";
import "./NAVDashboard.css";

const C = {
  border: "#e3e5df", text: "#243d35", muted: "#69766e", accent: "#28634d",
  bg: "#f6f5f1", red: "#ef4444", redBg: "rgba(239,68,68,.12)",
};

export default function CashRepatriation() {
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const currentRequest = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/reports/cash-repatriation/?as_of=${asOf}`);
      if (currentRequest === requestId.current) setReport(r.data);
    } catch (err) {
      console.error("Cash repatriation load failed:", err);
      if (currentRequest === requestId.current)
        setError(
          err.response?.data?.error ||
            "Could not load the cash repatriation report.",
        );
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  }, [asOf]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="nav-dashboard" aria-busy={loading}>
      <div className="nav-page-heading">
        <div>
          <div className="nav-eyebrow">TREASURY</div>
          <h1>Cash Owed</h1>
          <p style={{ color: C.muted, fontSize: 14, margin: "4px 0 0" }}>
            What each deal owes out of its own proceeds, and the cash to pay it.
            Nothing here moves money.
          </p>
        </div>
        <div className="nav-date-actions">
          <input
            aria-label="As-of date"
            required
            type="date"
            value={asOf}
            onChange={(e) => { if (e.target.value) setAsOf(e.target.value); }}
            style={{
              padding: "8px 12px", borderRadius: 6, border: `1px solid ${C.border}`,
              background: C.bg, color: C.text, fontSize: 13,
            }}
          />
          <button
            onClick={load}
            disabled={loading}
            style={{
              padding: "8px 20px", borderRadius: 6, border: "none",
              background: loading ? C.muted : C.accent, color: "#fff",
              fontSize: 13, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Reading…" : "Refresh"}
          </button>
        </div>
      </div>

      {error && (
        <div role="alert" style={{ padding: "16px 20px", borderRadius: 8, background: C.redBg, border: `1px solid ${C.red}33`, color: C.red, fontSize: 14, marginBottom: 20 }}>
          {error}
        </div>
      )}

      {loading && !report && (
        <div style={{ textAlign: "center", padding: 80, color: C.muted, fontSize: 16 }}>
          Reading the clearings…
        </div>
      )}

      {report && (
        <section className="nav-panel" style={{ padding: 24 }}>
          <RepatriationView report={report} />
        </section>
      )}
    </div>
  );
}
